import React, { useEffect, useMemo, useRef, useState } from 'react'
import logo from '../../assets/images/logo_final/logo.png'
import Plume from '../../assets/images/plume.png'
import PlumeRouge from '../../assets/images/plume-rouge.png'
import bulle from '../../assets/images/bulle.png'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CloseIcon from '@mui/icons-material/Close'
import './Menu.css'
import { io } from 'socket.io-client'
import { Search, ArrowDropDown, MenuOutlined, Close } from '@mui/icons-material'

import etourneau from '../../assets/images/image_oiseaux/etourneau.png'
import {
  deleteNotification,
  getNotifications,
  setNotificationWatched,
} from '../../api/Notifications'
import { verifyInfosAccountStripe } from '../../api/Paiement'

interface MenuProps {
  authState: {
    email: string
    connectedAccountID: string
    firstname: string
    lastname: string
    phoneNumber: string
    createdAt: Date
    updatedAt: Date
    userImg: string
    birthdate: string
    city: string
    isVisiteur: boolean
    isVerifiedStripeAccount: boolean
  }
  authProps: {
    token: string
    isLoggedIn: boolean
    setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>
  }
}

const Menu: React.FC<MenuProps> = ({ authState, authProps }) => {
  const { isLoggedIn, setIsLoggedIn } = authProps
  const [openBigMenu, setOpenBigMenu] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const [isProfileVisible, setIsProfileVisible] = useState(false) // État pour la visibilité du profil
  const profileCardRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const modalNotifRef = useRef<HTMLDivElement>(null)
  const [showTooltipNotVerified, setShowTooltipNotVerified] = useState(false)
  const [notifs, setNotifs] = useState<any[]>([])
  const [openNotifs, setOpenNotifs] = useState(false)
  const buttonNotifRef = useRef<HTMLButtonElement>(null)
  const [identityNotVerified, setIdentityNotVerified] = useState(false)
  const [stripeAccountTosAccepted, setStripeAccountTosAccepted] = useState(false)
  const [loadedStripeInfos, setLoadedStripeInfos] = useState(false)

  const serverAppDomain = process.env.REACT_APP_WEBSOCKET_URL
  const socket = useMemo(
    () => io(serverAppDomain as string, { transports: ['websocket'] }),
    [serverAppDomain],
  )

  const checkInfosStripeAccount = async () => {
    if (authState?.connectedAccountID) {
      const res = await verifyInfosAccountStripe(authState?.connectedAccountID)
      if (res) {
        setLoadedStripeInfos(true)
        if (res.data.tos?.date) setStripeAccountTosAccepted(true)
        if (res.data.refresh) setTimeout(() => window.location.reload(), 50)
        res.data.due.forEach((due: string) => {
          if (due === 'individual.verification.document') setIdentityNotVerified(true)
        })
      }
    }
  }

  useEffect(() => {
    checkInfosStripeAccount()
  }, [authState.connectedAccountID])

  const notifIsWatched = async (notifId: string) => {
    await setNotificationWatched(authProps.token, notifId)
  }
  const handleClickOutside = (event: any) => {
    if (
      buttonNotifRef?.current &&
      !buttonNotifRef?.current?.contains(event.target) &&
      modalNotifRef?.current &&
      !modalNotifRef?.current?.contains(event.target)
    ) {
      setOpenNotifs(false)
    }

    if (
      buttonRef?.current &&
      !buttonRef?.current?.contains(event.target) &&
      (!profileCardRef?.current || !profileCardRef?.current?.contains(event.target))
    ) {
      setIsProfileVisible(false)
    }
  }
  const deleteNotif = async (notifId: string) => {
    const res = await deleteNotification(authProps.token, notifId)
    if (res.success) {
      fetchNotifications()
    }
  }
  const fetchNotifications = async () => {
    if (authState.email) {
      const notifications = await getNotifications(authProps.token, authState.email)
      if (notifications.success) {
        setNotifs(notifications.notifications)
      }
    }
  }

  useEffect(() => {
    if (authState.email) {
      socket.emit('newUser', authState.email)
      fetchNotifications()
    }
  }, [authState.email, socket])

  useEffect(() => {
    setNotifs(notifs)
  }, [notifs])

  useEffect(() => {
    socket.on('newNotif', (data: any) => {
      if (data.userEmail === authState.email) setNotifs([data, ...notifs])
    })
    return () => {
      socket.off('newNotif')
    }
  }, [authState.email, notifs, socket])

  const handleResize = () => {
    if (window.innerWidth > 800) setOpenBigMenu(false)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('resize', handleResize)
      socket.disconnect()
    }
  }, [socket])

  const toggleProfile = () => {
    if (openNotifs) setOpenNotifs(false)
    setIsProfileVisible(!isProfileVisible)
  }

  const handleScroll = () => {
    const scrollPosition = window.scrollY
    const windowHeight = window.innerHeight
    if (scrollPosition > windowHeight * 0.1) {
      setIsScrolling(true)
    } else {
      setIsScrolling(false)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <>
      <header
        className={`header fixed top-0 left-0 z-50 w-full ${
          isScrolling
            ? 'bg-white/75 backdrop-blur-md shadow-sm border-b border-black/5'
            : `${
                authProps.isLoggedIn ? 'bg-[#FDDFCC]/90' : 'bg-white/70'
              } backdrop-blur-md border-b border-black/5`
        }`}
      >
        <div className='mx-auto flex h-[72px] w-full max-w-6xl items-center justify-between px-4 sm:px-6'>
          <div className='flex items-center gap-3'>
            <a href='/' className='flex items-center gap-2'>
              <img src={logo} alt='Logo Voyo' className='h-10 w-10' />
              <span className='hidden sm:block font-extrabold tracking-tight text-gray-900'>
                Voyo
              </span>
            </a>
          </div>

          <nav className='hidden md:flex items-center gap-8'>
            <a
              href='/programmation-visite'
              className={`menu-li-hover inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                window.location.pathname === '/programmation-visite'
                  ? 'text-gray-900 bg-black/5'
                  : 'text-gray-800 hover:bg-black/5'
              }`}
              aria-current={
                window.location.pathname === '/programmation-visite' ? 'page' : undefined
              }
            >
              <Search fontSize='small' /> Rechercher un visiteur
            </a>

            {!authState.isVisiteur ? (
              <a
                data-testid='homepage-linkmenu-devenir_visiteur'
                href='/inscription-visiteur'
                className='menu-li-hover inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-black/5'
              >
                <img src={Plume} alt='plume' className='h-5 w-5' /> Devenir visiteur
              </a>
            ) : (
              <div className='relative inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-[#f40000] bg-white/60'>
                <img src={PlumeRouge} alt='plume' className='h-5 w-5' />
                Vous êtes visiteur
                {loadedStripeInfos && (identityNotVerified || !stripeAccountTosAccepted) && (
                  <span
                    onClick={() => (window.location.href = '/dashboard')}
                    onMouseEnter={() => setShowTooltipNotVerified(true)}
                    onMouseLeave={() => setShowTooltipNotVerified(false)}
                  >
                    <ErrorOutlineIcon className='cursor-pointer text-orange-500 mb-0.5 animate-pulse' />
                  </span>
                )}
                {showTooltipNotVerified && (
                  <div className='absolute right-0 top-full mt-2 w-[260px] rounded-lg bg-white p-3 text-sm shadow-lg border border-gray-100'>
                    {identityNotVerified
                      ? 'Merci de faire vérifier votre identité auprès de notre partenaire Stripe.'
                      : 'Compte Stripe non validé. Merci de valider votre compte Stripe dans le dashboard.'}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className='flex items-center gap-2'>
            {isLoggedIn ? (
              <div className='user-picture-content relative flex items-center gap-2'>
                <button
                  ref={buttonNotifRef}
                  className='relative rounded-lg p-2 hover:bg-black/5 transition'
                  data-testid='button-link-inbox'
                  aria-label='Notifications'
                  onClick={() => {
                    if (isProfileVisible) setIsProfileVisible(false)
                    setOpenNotifs(!openNotifs)
                  }}
                >
                  {notifs?.filter((notif) => !notif.isWatched).length > 0 && (
                    <div className='absolute -top-1 -right-1 rounded-full px-1.5 py-0.5 bg-green-500 text-white text-[11px] font-bold'>
                      {notifs?.filter((notif) => !notif.isWatched).length}
                    </div>
                  )}
                  <img
                    src={bulle}
                    alt=''
                    className='h-7 w-7 opacity-80 hover:opacity-100 transition-opacity'
                  />
                </button>

                <button
                  className='user-picture flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-black/5 transition'
                  onClick={toggleProfile}
                  ref={buttonRef}
                >
                  <img src={authState.userImg ? authState.userImg : etourneau} alt='' />
                  <span className='first-letter:uppercase font-semibold text-sm text-gray-900'>
                    {authState.firstname}
                  </span>
                  <ArrowDropDown className='opacity-70' />
                </button>

                {/* Dropdown notifications (ancré au bouton) */}
                <div
                  className={`absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-24px)] rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden ${
                    openNotifs ? '' : 'hidden'
                  }`}
                  ref={modalNotifRef}
                >
                  <div className='px-4 py-3 border-b border-gray-100 flex items-center justify-between'>
                    <div className='text-sm font-bold text-gray-900'>Notifications</div>
                    <button
                      className='text-xs font-semibold text-gray-600 hover:text-gray-900'
                      onClick={() => setOpenNotifs(false)}
                    >
                      Fermer
                    </button>
                  </div>

                  <div className='max-h-[320px] overflow-auto'>
                    {notifs.length > 0 ? (
                      <div className='p-2'>
                        {notifs.map((notif: any) => (
                          <div
                            data-testid='li-link-inbox'
                            onClick={() => {
                              window.location.href = `/dashboard?chatId=${notif.content.chatId}`
                              notifIsWatched(notif.notifId)
                              setOpenNotifs(false)
                            }}
                            key={notif.id}
                            className={`group flex items-start justify-between gap-3 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                              notif.isWatched ? '' : 'bg-green-50'
                            }`}
                          >
                            <div className='flex gap-3'>
                              <img
                                src={notif.content.senderImg ? notif.content.senderImg : etourneau}
                                alt={`${notif.senderEmail}'s profile`}
                                className='w-10 h-10 rounded-full object-cover'
                              />
                              <div className='min-w-0'>
                                <div className='text-sm font-semibold text-gray-900'>
                                  {notif?.content?.firstName ? notif?.content?.firstName[0] : ''}
                                  {notif?.content?.lastName ? notif?.content?.lastName[0] : ''}
                                </div>
                                <div className='text-xs font-semibold text-gray-700'>
                                  {notif.content.type === 'message' ||
                                  notif.content.type === 'Visite' ? (
                                    <>
                                      {!notif.isWatched
                                        ? notif.content.type === 'message'
                                          ? 'Nouveau '
                                          : 'Nouvelle '
                                        : ''}
                                      {notif.content.type === 'message' ? (
                                        'message'
                                      ) : (
                                        <span>
                                          {`${notif.isWatched ? 'D' : 'd'}`}emande de visite
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>{notif.content.content}</>
                                  )}
                                </div>

                                {notif.content.content && notif.content.type === 'message' && (
                                  <div className='text-sm text-gray-600 truncate max-w-[260px]'>
                                    {notif.content.content}
                                  </div>
                                )}
                              </div>
                            </div>

                            <button
                              className='shrink-0 border bg-white border-gray-200 p-1.5 rounded-md hover:bg-gray-50 hover:border-gray-300 text-gray-600 hover:text-gray-900 opacity-0 group-hover:opacity-100 transition'
                              onClick={(e) => {
                                e.stopPropagation()
                                deleteNotif(notif.notifId)
                              }}
                              aria-label='Supprimer la notification'
                            >
                              <CloseIcon style={{ fontSize: 14 }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className='px-4 py-10 text-center'>
                        <div className='text-sm font-semibold text-gray-900'>Aucun message</div>
                        <div className='text-sm text-gray-600 mt-1'>
                          Les nouvelles demandes et messages apparaîtront ici.
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dropdown profil (ancré au bouton) */}
                <div
                  className={`absolute right-0 top-full mt-2 w-[240px] rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden ${
                    isProfileVisible ? '' : 'hidden'
                  }`}
                  ref={profileCardRef}
                >
                  <div className='px-4 py-3 border-b border-gray-100'>
                    <div className='text-sm font-bold text-gray-900'>Compte</div>
                    <div className='text-xs text-gray-600 truncate'>{authState.email}</div>
                  </div>

                  <ul className='p-2'>
                    {authState.isVisiteur && (
                      <li>
                        <a
                          href='/profil-visiteur'
                          className='flex w-full items-center rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50'
                          onClick={() => setIsProfileVisible(false)}
                        >
                          Mes paramètres
                        </a>
                      </li>
                    )}
                    <li>
                      <a
                        href='/programmation-visite'
                        className='flex w-full items-center rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50'
                        onClick={() => setIsProfileVisible(false)}
                      >
                        Planifier une visite
                      </a>
                    </li>
                    <li>
                      <a
                        href='/dashboard'
                        className='flex w-full items-center rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50'
                        onClick={() => setIsProfileVisible(false)}
                      >
                        Dashboard
                      </a>
                    </li>

                    <li>
                      <button
                        className='flex w-full items-center rounded-lg px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50'
                        onClick={() => {
                          setIsProfileVisible(false)
                          localStorage.removeItem('token')
                          setIsLoggedIn(false)
                          window.location.replace('/')
                        }}
                      >
                        Déconnexion
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                <a href='/inscription' className='block'>
                  <button className='start-button bg-orange'>Créer un compte</button>
                </a>
                <a href='/connexion' data-testid='button-inscription' className='block'>
                  <button className='start-button bg-orange'>Connexion</button>
                </a>
              </div>
            )}

            <button
              className='md:hidden rounded-lg p-2 hover:bg-black/5 transition'
              onClick={() => setOpenBigMenu(!openBigMenu)}
              aria-label='Ouvrir le menu'
            >
              {openBigMenu ? <Close /> : <MenuOutlined />}
            </button>
          </div>
        </div>

        {/* dropdowns déplacés et ancrés dans la zone user-picture-content */}
      </header>
      {openBigMenu && (
        <div className='fixed inset-0 z-40 bg-white/95 backdrop-blur-md flex justify-center items-center'>
          <ul className='text-2xl flex flex-col gap-10 text-center justify-center items-center'>
            {!authState.isVisiteur ? (
              <li className='relative h-[25.64px] ml-8 '>
                <a href='/inscription-visiteur' className='menu-li-hover  '>
                  <img src={Plume} alt='plume' className='w-7  absolute -left-[30px] top-3   ' />
                  Devenir visiteur
                </a>
                <div
                  className={`${
                    window.location.pathname === '/inscription-visiteur'
                      ? 'border-b-[3px]  w-[120%]  absolute -bottom-[5px] transfrom translate-x-[-15%] border-[#f75d00]'
                      : ''
                  }`}
                ></div>
              </li>
            ) : (
              <li className='relative h-[25.64px] ml-8 '>
                <div className='text-[#f40000] '>
                  <img
                    src={PlumeRouge}
                    alt='plume'
                    className='w-8 absolute -left-[30px] top-0.5 '
                  />
                  Vous êtes visiteur
                </div>
              </li>
            )}
            <li>
              <a href='/programmation-visite' className='reset-active menu-li-hover'>
                <Search /> Rechercher un visiteur
              </a>
            </li>

            {isLoggedIn && (
              <>
                {authState.isVisiteur && (
                  <li>
                    <a href='/profil-visiteur' className='reset-active menu-li-hover '>
                      Mes paramètres
                    </a>
                  </li>
                )}
                <li>
                  <a href='/programmation-visite' className='menu-li-hover'>
                    Planifier une visite
                  </a>
                </li>
                <li>
                  <a href='/dashboard' className='menu-li-hover  '>
                    Dashboard
                  </a>
                </li>

                <li>
                  <a href='#' className='menu-li-hover  '>
                    Infos
                  </a>
                </li>
              </>
            )}

            {!isLoggedIn ? (
              <>
                <li className='list-none'>
                  <a href='/inscription'>
                    <button className='start-button bg-tranparent'>Créer un compte</button>
                  </a>
                </li>
                <li className='list-none'>
                  <a href='/connexion'>
                    <button className='start-button bg-orange'>Connexion</button>
                  </a>
                </li>
              </>
            ) : (
              <li>
                <span
                  className='menu-li-hover cursor-pointer'
                  onClick={() => {
                    setIsProfileVisible(false)
                    localStorage.removeItem('token')
                    setIsLoggedIn(false)
                    window.location.replace('/')
                  }}
                >
                  Déconnexion
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
    </>
  )
}

export default Menu
